import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type {
  MasterListParams,
  MasterListResponse,
  MasterItemResponse,
} from '@/features/master/types';

export interface JobCategory {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

const BASE = '/job-categories';

export const jobCategoryQueries = {
  all: ['job-categories'] as const,
  list: (params: MasterListParams) =>
    queryOptions({
      queryKey: ['job-categories', 'list', params] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterListResponse<JobCategory>>(BASE, {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          query: params.query,
        });
        return data;
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['job-categories', 'detail', id] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterItemResponse<JobCategory>>(`${BASE}/${id}`);
        return data;
      },
    }),
};

export function useCreateJobCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<JobCategory, 'id'>) => {
      const { data } = await apiClient.post<MasterItemResponse<JobCategory>>(BASE, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: jobCategoryQueries.all }),
  });
}

export function useUpdateJobCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: JobCategory) => {
      const { data } = await apiClient.put<MasterItemResponse<JobCategory>>(`${BASE}/${id}`, input);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: jobCategoryQueries.all });
      qc.invalidateQueries({ queryKey: ['job-categories', 'detail', vars.id] });
    },
  });
}

export function useDeleteJobCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/${id}`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: jobCategoryQueries.all }),
  });
}
